// Cove Rewards wallet.
import { useEffect, useState } from "react";
import { customerApi, useCustomerAuth } from "@/lib/customerAuth";
import { Sparkles, TrendingUp, Clock } from "lucide-react";

const KIND_LABEL = { earn: "Earned", redemption: "Redeemed", adjustment: "Adjustment" };
const STATUS_COLOR = {
    available: "bg-jade/15 text-jade",
    pending: "bg-amber-100 text-amber-800",
    redeemed: "bg-navy/10 text-navy/70",
    expired: "bg-neutral-200 text-neutral-500",
    cancelled: "bg-red-100 text-red-700",
};

const TIERS = [
    { name: "Cove Member", min: 0 },
    { name: "Cove Preferred", min: 500 },
    { name: "Cove Elite", min: 2000 },
];

export default function AccountLoyalty() {
    const { user } = useCustomerAuth();
    const [data, setData] = useState({ balance: null, history: [] });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        customerApi.get("/customer/loyalty")
            .then((r) => setData(r.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="text-navy/60 text-sm">Loading your rewards…</div>;
    const balance = data.balance || {};
    const lifetime = balance.lifetime_earned || 0;
    const currentTier = TIERS.slice().reverse().find((t) => lifetime >= t.min) || TIERS[0];
    const nextTier = TIERS.find((t) => t.min > lifetime);
    const progress = nextTier ? Math.min(100, Math.round(((lifetime - currentTier.min) / (nextTier.min - currentTier.min)) * 100)) : 100;

    return (
        <div className="space-y-8" data-testid="account-loyalty">
            <div>
                <h2 className="font-display text-3xl text-navy">Cove Rewards</h2>
                <p className="text-navy/60 text-sm mt-1">Earn one point for every US dollar spent on room charges — no expiry for 2 years after your stay.</p>
            </div>

            {/* Balance hero */}
            <section className="bg-gradient-to-br from-navy via-navy to-jade text-ivory rounded-2xl p-8 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-gold/25 rounded-full blur-3xl -translate-y-1/3 translate-x-1/4" />
                <div className="relative flex items-start justify-between gap-6 flex-wrap">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.28em] text-gold flex items-center gap-2"><Sparkles size={12} /> {currentTier.name}</div>
                        <div className="mt-4 font-display text-6xl">{balance.available || 0}</div>
                        <div className="text-ivory/70 text-sm mt-1">available points · {user?.email}</div>
                    </div>
                    <div className="text-right">
                        {balance.pending > 0 && <div className="text-sm text-ivory/70"><span className="text-gold font-display text-xl">{balance.pending}</span> pending from active stays</div>}
                        <div className="text-[11px] text-ivory/60 mt-2">Lifetime earned: {lifetime}</div>
                    </div>
                </div>

                {/* Tier progress */}
                {nextTier && (
                    <div className="mt-8 relative">
                        <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.18em] text-ivory/70 mb-2">
                            <span>{currentTier.name}</span>
                            <span>{nextTier.name} · {nextTier.min - lifetime} pts to go</span>
                        </div>
                        <div className="h-2 bg-ivory/10 rounded-full overflow-hidden">
                            <div className="h-full bg-gold transition-all duration-500" style={{ width: `${progress}%` }} />
                        </div>
                    </div>
                )}
            </section>

            {/* Sub balances */}
            <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Mini label="Available" value={balance.available || 0} icon={<Sparkles size={14} className="text-jade" />} />
                <Mini label="Pending" value={balance.pending || 0} icon={<Clock size={14} className="text-amber-500" />} />
                <Mini label="Redeemed" value={balance.redeemed || 0} icon={<TrendingUp size={14} className="text-navy" />} />
                <Mini label="Expired" value={balance.expired || 0} icon={<Clock size={14} className="text-neutral-400" />} />
            </section>

            {/* History */}
            <section>
                <h3 className="font-display text-2xl text-navy mb-4">Recent activity</h3>
                <div className="bg-white border border-navy/10 rounded-2xl overflow-hidden">
                    {data.history.length === 0 ? (
                        <div className="p-8 text-center text-navy/50 text-sm">No activity yet. Book a direct stay to start earning.</div>
                    ) : (
                        <table className="w-full text-sm" data-testid="loyalty-history">
                            <thead className="bg-navy/5 text-[10px] uppercase tracking-[0.18em] text-navy/60">
                                <tr>
                                    <th className="text-left px-5 py-3">Date</th>
                                    <th className="text-left px-5 py-3">Description</th>
                                    <th className="text-left px-5 py-3">Status</th>
                                    <th className="text-right px-5 py-3">Points</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.history.map((h) => (
                                    <tr key={h.id} className="border-t border-navy/10">
                                        <td className="px-5 py-3 text-navy/60 text-[12px] whitespace-nowrap">
                                            {new Date(h.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                                        </td>
                                        <td className="px-5 py-3 text-navy">
                                            <div>{h.reason || KIND_LABEL[h.kind]}</div>
                                            <div className="text-[10px] uppercase tracking-[0.14em] text-navy/40 mt-0.5">{h.kind}</div>
                                        </td>
                                        <td className="px-5 py-3">
                                            <span className={`text-[10px] uppercase tracking-[0.14em] px-2 py-1 rounded-full ${STATUS_COLOR[h.status] || "bg-navy/10 text-navy/70"}`}>{h.status}</span>
                                        </td>
                                        <td className="px-5 py-3 text-right font-display text-navy">
                                            <span className={h.points > 0 ? "text-jade" : "text-red-600"}>{h.points > 0 ? "+" : ""}{h.points}</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </section>

            <p className="text-[11px] text-navy/50">
                Point redemption launches soon — you'll be able to spend points on room discounts, upgrades, spa credit, and late check-outs.
            </p>
        </div>
    );
}

function Mini({ label, value, icon }) {
    return (
        <div className="bg-white border border-navy/10 rounded-xl p-4">
            <div className="flex items-center gap-2">{icon}<span className="text-[10px] uppercase tracking-[0.18em] text-navy/60">{label}</span></div>
            <div className="mt-2 font-display text-2xl text-navy">{value}</div>
        </div>
    );
}
